import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { CompanyAccessSummary } from '@handover/shared-types';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { formatBillingStatusLabel, getWorkspaceStatusMessage } from '../../helpers/companyAccessHelper';

type WorkspaceLockedScreenProps = {
  accessSummary?: CompanyAccessSummary | null;
  supportEmail?: string;
  isAdmin: boolean;
  canManageBilling: boolean;
};

export const WorkspaceLockedScreen = ({
  accessSummary,
  supportEmail,
  isAdmin,
  canManageBilling,
}: WorkspaceLockedScreenProps) => {
  const billingStatus = accessSummary?.billingStatus;

  const getTitle = () => {
    if (billingStatus === 'inactive') {
      return canManageBilling ? 'Billing setup required' : 'Payment required';
    }

    if (billingStatus === 'unpaid' || billingStatus === 'canceled') {
      return canManageBilling ? 'Payment required to restore access' : 'Please contact your workspace manager';
    }

    return canManageBilling ? 'Workspace access is limited' : 'Workspace temporarily unavailable';
  };

  const getMessage = () => {
    if (billingStatus === 'inactive') {
      return canManageBilling
        ? 'This workspace needs an active subscription before anyone can continue. Open Billing to finish setup and activate the workspace.'
        : 'This workspace needs an active subscription before you can continue. Please contact your workspace manager or super admin for billing help.';
    }

    if (billingStatus === 'unpaid') {
      return canManageBilling
        ? 'Payment could not be collected. Open Billing to update the payment method or subscription and restore access.'
        : 'This workspace is temporarily unavailable because payment could not be collected. Please contact your workspace manager or super admin for billing help.';
    }

    if (billingStatus === 'canceled') {
      return canManageBilling
        ? 'This workspace subscription is no longer active. Open Billing to restart the subscription and restore access.'
        : 'This workspace is inactive because the subscription is no longer active. Please contact your workspace manager or super admin for billing help.';
    }

    return (
      getWorkspaceStatusMessage(accessSummary) ||
      'This workspace is not currently available.'
    );
  };

  const title = getTitle();
  const message = getMessage();

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: 680,
          width: '100%',
          p: { xs: 3, md: 5 },
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            mx: 'auto',
            mb: 3,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'error.light',
            color: 'error.contrastText',
          }}
        >
          <LockOutlinedIcon fontSize="large" />
        </Box>
        <Typography variant="h4" fontWeight={800} sx={{ mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {message}
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2.5,
            textAlign: 'left',
            backgroundColor: 'background.default',
          }}
        >
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
            What you can do next
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {canManageBilling
              ? billingStatus === 'inactive'
                ? 'Open Billing to complete setup and activate the workspace.'
                : 'Open Billing to update payment details or manage the subscription, then return once the workspace is active again.'
              : 'Contact your workspace manager or super admin for billing help.'}
          </Typography>
        </Paper>
        {accessSummary?.billingStatus && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Billing status: {formatBillingStatusLabel(accessSummary.billingStatus)}
          </Typography>
        )}
        {isAdmin && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {canManageBilling
              ? 'Admins can still open Billing to review the workspace status during a billing lock.'
              : 'Billing changes must be handled by an admin or super admin.'}
          </Typography>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
          {canManageBilling ? (
            <Button component={RouterLink} to="/billing" variant="contained">
              {billingStatus === 'inactive' ? 'Open Billing Setup' : 'Open Billing'}
            </Button>
          ) : null}
          {canManageBilling && supportEmail ? (
            <Button href={`mailto:${supportEmail}`} variant="outlined">
              Contact support
            </Button>
          ) : null}
          {!canManageBilling && supportEmail ? (
            <Button href={`mailto:${supportEmail}`} variant="outlined">
              Email support
            </Button>
          ) : null}
        </Stack>
      </Paper>
    </Box>
  );
};
